import { FC, useCallback } from "react";
import { FormGroup } from "@material-ui/core";
import { includes } from "lodash";
import { usePostHog } from "posthog-js/react";

import { CheckboxField, Modal } from "components";
import ParagraphContent from "components/Modal/ParagraphContent";
import { UiParam, UiParams } from "hooks/useQueryParams/types";
import { EXCHANGE_FILTER_SORT } from "services/postHog/constants";
import theme from "theme";

import FilterSectionTitle from "./FilterSectionTitle";

interface CheckboxFilterGroupConfig {
  title: string;
  filterOptions: UiParam[];
  key: string;
  makePostHogFilterBy: (optionName: string) => string;
}

interface CheckboxFilterGroupModalProps {
  title: string;
  label: string[];
}

interface CheckboxFilterGroupProps {
  filterConfig: CheckboxFilterGroupConfig;
  uiParams: UiParams;
  setUiParams: (setState: (uiParams: UiParams) => UiParams) => void;
  modalInfo: CheckboxFilterGroupModalProps;
}

export const CheckboxFilterGroup: FC<CheckboxFilterGroupProps> = ({
  filterConfig,
  uiParams,
  setUiParams,
  modalInfo,
}) => {
  const posthog = usePostHog();
  const handleToggleFilter = useCallback(
    (checked: boolean, optionValue: string[], optionName: string) => {
      posthog?.capture(EXCHANGE_FILTER_SORT, {
        actionType: checked ? "checked" : "unchecked",
        filterBy: filterConfig.makePostHogFilterBy(optionName),
      });
      setUiParams(prev => {
        const prevValue = prev[filterConfig.key].value as string[];
        const updatedValue = checked
          ? [...prevValue, ...optionValue]
          : prevValue.filter(value => !optionValue.includes(value));
        return {
          ...prev,
          page: { ...prev.page, value: "1" },
          [filterConfig.key]: {
            ...prev[filterConfig.key],
            value: updatedValue,
          },
        };
      });
    },
    [posthog, setUiParams, filterConfig],
  );

  return (
    <>
      <FilterSectionTitle
        title={filterConfig.title}
        HelpModal={({ onClose }) => (
          <Modal open onClose={onClose} title={modalInfo.title}>
            <ParagraphContent paragraphs={modalInfo.label} />
          </Modal>
        )}
      />
      <FormGroup>
        {filterConfig.filterOptions.map(option => {
          return (
            <CheckboxField
              dense
              key={option.name}
              checked={includes(
                uiParams[filterConfig.key].value,
                option.value[0],
              )}
              onChange={(_, checked) =>
                handleToggleFilter(
                  checked,
                  option.value as string[],
                  option.name,
                )
              }
              label={option.name}
              checkboxColor={theme.custom.palette.purple.main}
            />
          );
        })}
      </FormGroup>
    </>
  );
};

export default CheckboxFilterGroup;
